if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input[type="search"]');
      this.resetButtons = this.querySelectorAll('button[type="reset"]');
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.statusElement = this.querySelector('.predictive-search__status');
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.isOpen = false;
      this.emptyStateLoaded = false;

      if (this.input) {
        this.bindEvents();
      }
    }

    connectedCallback() {
      this._modal = this.closest('theme-modal');
      if (this._modal) {
        this._modalOpenUnsub = theme.pubsub.subscribe(theme.pubsub.events.MODAL_OPEN, (data) => {
          if (data.id === this._modal.id) this.onModalOpen();
        });
        this._modalCloseUnsub = theme.pubsub.subscribe(theme.pubsub.events.MODAL_CLOSE, (data) => {
          if (data.id === this._modal.id) this.onModalClose();
        });

        this._modal.querySelectorAll('[class*="__close"]').forEach((btn) => {
          btn.addEventListener('click', () => this._modal.close());
        });
      }

      if (this.dataset.recentlyViewed === 'true' && theme.recentlyViewed?.hasProducts()) {
        const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
        idle(() => {
          this.loadEmptyState();
        });
      }
    }

    disconnectedCallback() {
      this._modalOpenUnsub?.();
      this._modalCloseUnsub?.();
    }

    onModalOpen() {
      if (!this.emptyStateLoaded && !this.searchTerm.length) {
        this.loadEmptyState();
      }
    }

    onModalClose() {
      this.resetSearch();
    }

    bindEvents() {
      this.input.addEventListener(
        'input',
        theme.utils.debounce(this.onChange.bind(this), 300)
      );
      this.input.form?.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.form?.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-clear-recently-viewed]')) {
          e.preventDefault();
          this.clearRecentlyViewed();
        }
      });
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      const newSearchTerm = this.getQuery();

      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results')?.remove();
      }

      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;
      this.toggleResetButton();

      if (!this.searchTerm.length) {
        this.emptyStateLoaded = false;
        this.loadEmptyState();
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    toggleResetButton() {
      if (!this.resetButtons || !this.resetButtons.length) return;
      this.resetButtons.forEach(button => {
        button.classList.toggle('hidden', this.input.value.length === 0);
      });
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
      }
    }

    onFormReset(event) {
      event.preventDefault();
      if (!this.querySelector('[aria-selected="true"] a')) {
        this.resetSearch();
        this.input.focus();
      }
    }

    resetSearch() {
      this.input.value = '';
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.toggleResetButton();
      this.emptyStateLoaded = false;
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();

      if (!currentSearchTerm.length) {
        if (!this.emptyStateLoaded) {
          this.loadEmptyState();
        }
        return;
      }

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onKeyup(event) {
      if (!this.getQuery().length && event.key !== 'Escape') {
        if (!this.emptyStateLoaded) {
          this.loadEmptyState();
        }
      }

      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          this.switchOption('up');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }

    onKeydown(event) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      if (!searchForTextElement || !previousTerm) return;

      const currentText = searchForTextElement.textContent;
      if (currentText.includes(previousTerm)) {
        searchForTextElement.textContent = currentText.replace(previousTerm, newTerm);
      }
    }

    switchOption(direction) {
      if (!this.hasAttribute('open')) return;

      const selectedElement = this.querySelector('[aria-selected="true"]');
      const allElements = Array.from(
        this.querySelectorAll('[data-ps-option]')
      ).filter((el) => el.offsetParent !== null);

      if (allElements.length === 0) return;

      let currentIndex = selectedElement ? allElements.indexOf(selectedElement) : -1;
      let newIndex;

      if (direction === 'up') {
        newIndex = currentIndex <= 0 ? allElements.length - 1 : currentIndex - 1;
      } else {
        newIndex = currentIndex >= allElements.length - 1 ? 0 : currentIndex + 1;
      }

      if (selectedElement) {
        selectedElement.setAttribute('aria-selected', 'false');
      }

      const newElement = allElements[newIndex];
      newElement.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', newElement.id);

      const sliderEl = newElement.closest('theme-slider.scrollable');
      if (sliderEl?.engine) {
        const slides = Array.from(sliderEl.querySelectorAll('.slider__item'));
        const slideIndex = slides.indexOf(newElement.closest('.slider__item'));
        if (slideIndex !== -1) {
          const perScroll = sliderEl.engine.opts.slidesToScroll || 1;
          sliderEl.engine.goTo(Math.floor(slideIndex / perScroll));
        }
      } else {
        newElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    selectOption() {
      const selectedOption = this.querySelector('[aria-selected="true"] a, a[aria-selected="true"]');
      if (selectedOption) {
        selectedOption.click();
      }
    }

    async loadEmptyState() {
      if (this.emptyStateLoaded) return;

      const enableRecentlyViewed = this.dataset.recentlyViewed === 'true';
      const hasRecentlyViewed = enableRecentlyViewed && theme.recentlyViewed?.hasProducts();

      this.setLiveRegionLoadingState();

      try {
        const emptySectionMarkup = await this.fetchEmptyStateCollection();

        if (hasRecentlyViewed) {
          const recentlyViewedMarkup = await this.fetchRecentlyViewed();

          if (recentlyViewedMarkup) {
            const emptyDoc = new DOMParser().parseFromString(emptySectionMarkup, 'text/html');
            const recentlyDoc = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');

            const recentlyViewedProducts = recentlyDoc.querySelector('#predictive-search-products') ||
                                           recentlyDoc.querySelector('.predictive-search__recently-viewed');

            if (recentlyViewedProducts) {
              const emptyContainer = emptyDoc.querySelector('#predictive-search-results') ||
                                     emptyDoc.querySelector('.predictive-search__results');

              if (emptyContainer) {
                const existingProducts = emptyContainer.querySelector('#predictive-search-products');
                if (existingProducts) {
                  existingProducts.insertAdjacentElement('beforebegin', recentlyViewedProducts);
                } else {
                  emptyContainer.prepend(recentlyViewedProducts);
                }

                this.predictiveSearchResults.innerHTML = emptyContainer.outerHTML;
                this.emptyStateLoaded = true;
                this.removeAttribute('loading');
                this.open();
                return;
              } else {
                this.predictiveSearchResults.innerHTML = recentlyViewedMarkup;
                this.emptyStateLoaded = true;
                this.removeAttribute('loading');
                this.open();
                return;
              }
            }
          }
        }

        if (emptySectionMarkup) {
          const emptyDoc = new DOMParser().parseFromString(emptySectionMarkup, 'text/html');
          const content = emptyDoc.querySelector('#predictive-search-results') ||
                         emptyDoc.querySelector('.predictive-search__results') ||
                         emptyDoc.body;

          this.predictiveSearchResults.innerHTML = content.innerHTML || content.outerHTML;
          this.emptyStateLoaded = true;
          this.removeAttribute('loading');
          this.open();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.removeAttribute('loading');
      }
    }

    async fetchRecentlyViewed() {
      const products = theme.recentlyViewed.getProducts();
      if (!products.length) return null;

      const productPromises = products.map(async (product) => {
        try {
          const response = await fetch(`/products/${product.handle}.js`, {
            signal: this.abortController.signal
          });
          if (!response.ok) return null;
          return response.json();
        } catch (e) {
          return null;
        }
      });

      const fetchedProducts = (await Promise.all(productPromises)).filter(Boolean);
      if (fetchedProducts.length === 0) return null;

      return this.renderRecentlyViewedHTML(fetchedProducts);
    }

    renderRecentlyViewedHTML(products) {
      const recentlyViewedTitle = this.dataset.recentlyViewedTitle;
      const clearText = this.dataset.clearText;

      const productItems = products.map((product, index) => {
        const image = product.featured_image || product.images?.[0];
        const imageHtml = image
          ? `<div class="predictive-search__product-image">
              <img
                src="${this.getSizedImageUrl(image, '300x')}"
                alt="${this.escapeHtml(product.title)}"
                width="150"
                loading="lazy"
              >
            </div>`
          : '';

        const price = this.formatMoney(product.price);
        const comparePrice = product.compare_at_price > product.price
          ? `<span class="predictive-search__price--compare">${this.formatMoney(product.compare_at_price)}</span>`
          : '';
        const priceClass = product.compare_at_price > product.price ? 'predictive-search__price--sale' : '';

        return `
          <li
            id="predictive-search-option-recently-viewed-${index + 1}"
            class="predictive-search__product-card"
            role="option"
            aria-selected="false"
            data-ps-option
          >
            <a href="${product.url}" class="predictive-search__product-link" tabindex="-1">
              ${imageHtml}
              <div class="predictive-search__product-info">
                <span class="predictive-search__product-title">
                  ${this.escapeHtml(product.title)}
                </span>
                <span class="predictive-search__product-price">
                  <span class="${priceClass}">${price}</span>
                  ${comparePrice}
                </span>
              </div>
            </a>
          </li>
        `;
      }).join('');

      return `
        <div id="predictive-search-results" class="predictive-search__results">
          <div id="predictive-search-products" class="predictive-search__group predictive-search__group--products predictive-search__recently-viewed">
            <div class="predictive-search__group-header">
              <p class="predictive-search__heading" id="predictive-search-recently-viewed">
                ${recentlyViewedTitle}
              </p>
              <button
                type="button"
                class="predictive-search__clear-button"
                data-clear-recently-viewed
              >
                ${clearText}
              </button>
            </div>
            <ul
              class="predictive-search__products-grid"
              role="group"
              aria-labelledby="predictive-search-recently-viewed"
            >
              ${productItems}
            </ul>
          </div>
        </div>
      `;
    }

    getSizedImageUrl(src, size) {
      if (!src) return '';
      const url = src.includes('//') ? src : `https:${src}`;
      return url.replace(/\.(jpg|jpeg|png|gif|webp)/i, `_${size}.$1`);
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    formatMoney(cents) {
      if (theme.utils?.formatMoney) {
        return theme.utils.formatMoney(cents);
      }
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents);
      }
      const amount = (cents / 100).toFixed(2);
      const currency = theme.currency?.active || 'EUR';
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    }

    async fetchEmptyStateCollection() {
      const sectionId = this.dataset.emptySectionId || 'predictive-search-empty';
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), { signal: this.abortController.signal });
      if (!response.ok) throw new Error(response.status);

      return response.text();
    }

    clearRecentlyViewed() {
      if (theme.recentlyViewed) {
        theme.recentlyViewed.clearProducts();

        const wrapper = this.querySelector('.predictive-search__recently-viewed');
        if (wrapper) {
          wrapper.classList.add('is-removing');
          setTimeout(() => {
            wrapper.remove();
          }, 300);
        }
      }
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
      const limit = this.dataset.limit || 6;
      const limitScope = this.dataset.limitScope || 'each';
      const resourceTypes = this.dataset.resourceTypes || '';

      this.setLiveRegionLoadingState();

      const cacheKey = `psearch:${queryKey}`;
      const cachedMarkup = theme.cache?.get(cacheKey);
      if (cachedMarkup) {
        this.renderSearchResults(cachedMarkup);
        return;
      }

      const resourceTypesParam = resourceTypes
        ? `&resources[type]=${encodeURIComponent(resourceTypes)}`
        : '';

      fetch(
        `${theme.routes.predictiveSearchUrl}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[limit]=${limit}&resources[limit_scope]=${limitScope}${resourceTypesParam}`,
        { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search')?.innerHTML;

          if (resultsMarkup) {
            if (resultsMarkup.length <= (theme.cache?.MAX_CACHEABLE_LENGTH ?? Infinity)) theme.cache?.set(cacheKey, resultsMarkup);
            this.renderSearchResults(resultsMarkup);
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          this.close();
        });
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search__status');
      const loadingText = this.getAttribute('data-loading-text');
      this.setLiveRegionText(loadingText);
      this.setAttribute('loading', '');
    }

    setLiveRegionText(text) {
      if (!this.statusElement) return;
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = text;
      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', 'true');
      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      this.removeAttribute('loading');
      const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (countElement) {
        this.setLiveRegionText(countElement.textContent);
      }
    }

    open() {
      this.setAttribute('open', '');
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }

      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
